"use client";

import React from "react";
import { Activity, Flame, Gauge, HeartPulse, Compass, Users, CircleCheck as CheckCircle2, ArrowRight, TrendingUp, Footprints, Bike } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TrainingDecisions() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const applications = [{
    title: "Running & Athletics",
    icon: Footprints,
    description: "Endurance training, pacing benchmarks, and running economy analysis."
  }, {
    title: "Cycling & Triathlon",
    icon: Bike,
    description: "Cadence-specific power zones, sustained aerobic output, and multi-sport balance."
  }, {
    title: "Field & Court Sports",
    icon: Activity,
    description: "Intermittent high-intensity recovery capacity and baseline work capacity."
  }, {
    title: "Return to Conditioning",
    icon: Compass,
    description: "Safe, graduated load reintroduction following injury, illness, or hiatus."
  }, {
    title: "Monitoring Progress",
    icon: TrendingUp,
    description: "Periodic reassessment to track physiological adaptation over training blocks."
  }];
  const informedAreas = [{
    title: "Personalised intensity zones",
    description: "Calibrated heart rate and effort ranges derived from respiratory exchange rather than population formulas.",
    icon: Gauge
  }, {
    title: "Aerobic conditioning",
    description: "Specific training thresholds to build an efficient base engine and mitochondrial capacity.",
    icon: HeartPulse
  }, {
    title: "Threshold-focused training",
    description: "Pinpointed lactate and ventilatory transitions to structure targeted tempo and interval work.",
    icon: Activity
  }, {
    title: "Recovery & easier intensities",
    description: "Clear demarcation of genuine recovery paces to prevent inadvertent over-reaching.",
    icon: CheckCircle2
  }, {
    title: "Fuelling considerations",
    description: "Understanding substrate utilisation (carbohydrate vs. fat oxidation) across varying exercise intensities.",
    icon: Flame
  }];
  return <section id="trainingdecisionsandinclusivity" data-nav="light" ref={sectionRef} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Part A: Turn testing into better training decisions */}
        <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="max-w-3xl mb-12">
            <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
              Clinical Assessment
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
              Turn testing into better training decisions
            </h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              Objective physiological assessment provides precise physiological data to inform structured programmes. 
              Testing does not guarantee performance outcomes, but it removes guesswork from your training variables, 
              workloads, and recovery phases.
            </p>
          </div>

          {/* Contexts and Sports Covered */}
          <div className="mb-14">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Disciplines &amp; Clinical Scenarios
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {applications.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.title} data-index={i} className="p-5 rounded-lg border border-border bg-card/60 hover:bg-card transition-colors">
                    <div className="size-9 rounded-md bg-secondary flex items-center justify-center text-primary mb-3">
                      <IconComponent className="size-5" />
                    </div>
                    <h4 className="font-medium text-foreground text-sm mb-1">{item.title}</h4>
                    <p className="text-xs text-muted-foreground leading-normal">{item.description}</p>
                  </div>;
            })}
            </div>
          </div>

          {/* 5 Specific Testing Insights */}
          <div className="mb-20">
            <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground mb-6">
              What physiological testing informs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {informedAreas.map((area, i) => {
              const IconComp = area.icon;
              return <Card key={area.title} data-index={i} className="border-border shadow-xs hover:border-primary/40 transition-colors">
                    <CardHeader className="pb-2">
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {area.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {area.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>

        {/* Part B: Inclusivity & Non-Elite Focus */}
        <div className={`rounded-2xl border border-border bg-card p-8 sm:p-12 lg:p-14 shadow-sm transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium mb-4">
                <Users className="size-3.5" />
                <span>Open to Everyone</span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground mb-4">
                You do not need to be an elite athlete
              </h3>
              <p className="text-base sm:text-lg text-foreground/90 font-medium leading-relaxed mb-4">
                VO₂ testing can be useful for anyone who wants to understand their aerobic fitness more objectively, 
                whether they are beginning structured exercise, returning to fitness, or training competitively.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our clinic applies the same physiological precision whether you are managing cardiovascular health, 
                rebuilding conditioning after illness, or balancing busy weekly training with work commitments. 
                Testing parameters are calibrated directly to individual starting baselines.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center lg:items-end">
              <Button asChild size="lg" className="w-full sm:w-auto lg:w-full justify-center">
                <Link to="#">
                  Book an assessment
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto lg:w-full justify-center">
                <Link to="#">
                  Explore clinical services
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}